import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Chip,
    LinearProgress,
    Alert,
    Container,
    Stack,
    Tooltip
} from "@mui/material";
import { School, CheckCircle, Info } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function LearningPathsPage() {
    const navigate = useNavigate();
    const { userProfile } = useAuth();
    const [paths, setPaths] = useState([]);
    const [progresses, setProgresses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const pathsData = await learningService.getLearningPaths(true);
                setPaths(pathsData);

                if (userProfile?.uid) {
                    const progressData = await learningService.getStudentProgressSummary(
                        userProfile.uid
                    );
                    setProgresses(progressData);
                }
            } catch (error) {
                console.error("Error fetching learning paths:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userProfile]);

    const truncateText = (text, maxLength = 100) => {
        if (!text) return "";
        if (text.length > maxLength) {
            return text.substring(0, maxLength) + "...";
        }
        return text;
    };

    const getPathProgress = (pathId) => {
        const progress = progresses.find((p) => p.pathId === pathId);
        if (!progress) return 0;

        const levels = progress.levelStatus || {};
        const clearedLevels = Object.values(levels).filter((s) => s.cleared).length;
        const totalLevels = Object.keys(levels).length || 1;

        return (clearedLevels / totalLevels) * 100;
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            Beginner: "success",
            Intermediate: "warning",
            Advanced: "error"
        };
        return colors[difficulty] || "default";
    };

    const isPathCompleted = (pathId) => {
        const progress = progresses.find((p) => p.pathId === pathId);
        return progress?.pathCompleted || false;
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Paths">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Learning Paths">
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Box sx={{ mb: 4 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: "#003366" }}>
                        Learning Paths
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Enhance your skills and earn credits through structured learning paths
                    </Typography>
                </Box>

                {paths.length === 0 ? (
                    <Alert severity="info">No learning paths available yet</Alert>
                ) : (
                    <Grid container spacing={2.5}>
                        {paths.map((path) => {
                            const progress = getPathProgress(path.id);
                            const completed = isPathCompleted(path.id);

                            return (
                                <Grid item xs={12} sm={6} lg={3} key={path.id}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 2,
                                            transition: "all 0.2s ease",
                                            "&:hover": {
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)"
                                            },
                                            position: "relative",
                                            overflow: "hidden"
                                        }}
                                    >
                                        {completed && (
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    top: 8,
                                                    right: 8,
                                                    background: "#003366",
                                                    color: "white",
                                                    p: 0.5,
                                                    borderRadius: 999,
                                                    zIndex: 10
                                                }}
                                            >
                                                <CheckCircle sx={{ width: 18, height: 18 }} />
                                            </Box>
                                        )}

                                        <CardContent sx={{ flexGrow: 1, p: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 1,
                                                    mb: 1
                                                }}
                                            >
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{ fontWeight: 700, flex: 1, color: "#003366", lineHeight: 1.3 }}
                                                >
                                                    {path.name}
                                                </Typography>
                                                <Tooltip title={path.description}>
                                                    <Info
                                                        sx={{
                                                            fontSize: 18,
                                                            color: "primary.main",
                                                            cursor: "help"
                                                        }}
                                                    />
                                                </Tooltip>
                                            </Box>

                                            <Tooltip title={path.description} arrow>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        mb: 1.5,
                                                        minHeight: 34,
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical"
                                                    }}
                                                >
                                                    {truncateText(path.description, 100)}
                                                </Typography>
                                            </Tooltip>

                                            <Stack direction="row" spacing={0.75} sx={{ mb: 1.5, flexWrap: "wrap" }}>
                                                <Chip
                                                    label={path.difficulty}
                                                    size="small"
                                                    color={getDifficultyColor(path.difficulty)}
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    icon={<School />}
                                                    label={`${path.creditsOnCompletion} Credits`}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                />
                                                <Chip
                                                    label={`${path.levels?.length || 0} Levels`}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Stack>

                                            <Box sx={{ mb: 1.5 }}>
                                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Progress
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: "#003366" }}>
                                                        {Math.round(progress)}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={progress}
                                                    sx={{
                                                        height: 6,
                                                        borderRadius: 4,
                                                        backgroundColor: "action.disabledBackground"
                                                    }}
                                                />
                                            </Box>

                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                {path.duration}
                                            </Typography>
                                        </CardContent>

                                        <CardActions sx={{ p: 1.5, pt: 0 }}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                fullWidth
                                                onClick={() =>
                                                    navigate(`/student/learning/paths/${path.id}`)
                                                }
                                                sx={{
                                                    backgroundColor: "#003366",
                                                    "&:hover": { backgroundColor: "#02264a" }
                                                }}
                                            >
                                                {completed ? "Review" : progress > 0 ? "Continue" : "Start"}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Container>
        </AdminLayout>
    );
}
